import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAppNameFromPageSlug, getPromptTemplates } from 'chatspot-core/utils/PromptTemplateUtils';
import { fetchAllFavoritePrompts, fetchAllPromptTemplates, fetchRoutePriorities, removeFavoritePrompt, saveFavoritePrompt } from '../api/promptTemplatesApi';
const sortFavoriteTemplates = favoriteTemplates => {
  return favoriteTemplates.sort((a, b) => {
    const dateA = new Date(Number(a.createdAt)).getTime();
    const dateB = new Date(Number(b.createdAt)).getTime();
    return dateB - dateA;
  });
};
export const refetchAndFormatFeaturedTemplates = createAsyncThunk('promptTemplates/reFetchFeaturedTemplates', ({
  pathname,
  templates
}) => {
  const appName = getAppNameFromPageSlug(pathname);
  const slug = appName || pathname;
  const promptLookupObject = templates.reduce((acc, prompt) => {
    acc[prompt.id] = prompt;
    return acc;
  }, {});
  return fetchRoutePriorities(slug).then(routePrioritiesResponse => {
    const featuredTemplateIds = routePrioritiesResponse.templatePriorities[0].templateIds;
    const featuredTemplates = featuredTemplateIds.map(templateId => {
      return promptLookupObject[templateId];
    })
    // Check that they exist, in case template was deleted but the category was not updated.
    .filter(Boolean);
    return {
      featuredTemplates
    };
  });
});
// This versions works with new API, and is dynamic based off of ChadMin template ordering
export const fetchPromptTemplates = createAsyncThunk('promptTemplates/fetchPromptTemplates', ({
  pathname,
  locale
}) => {
  const appName = getAppNameFromPageSlug(pathname);
  // TODO: Double check this.  Edge case around editors.
  const slug = appName || pathname;
  return Promise.all([fetchAllPromptTemplates(locale), fetchRoutePriorities(slug), fetchAllFavoritePrompts()]).then(([allPromptTemplatesResponse, routePrioritiesResponse, favoritesResponse]) => {
    var _categoryPriorities$f;
    const {
      promptTemplates,
      categoryPriorities
    } = allPromptTemplatesResponse;
    // Keeping the same formatting as legacy.
    const formattedPromptTemplates = getPromptTemplates(
    // TODO - @mlegocki: fix TS typing in chatspot-core to remove coercion
    promptTemplates.results);
    const favoritesLookupObject = favoritesResponse.reduce((acc, favorite) => {
      acc[favorite.prompt] = favorite.id;
      return acc;
    }, {});
    const formattedPromptTemplatesWithFavorites = formattedPromptTemplates.map(template => {
      const favoriteId = favoritesLookupObject[template.legacyPrompt];
      return Object.assign({}, template, {
        isFavorite: Boolean(favoriteId),
        favoriteId
      });
    });
    const favoritePromptTemplates = favoritesResponse.map(favorite => {
      return formattedPromptTemplatesWithFavorites.find(template => template.legacyPrompt === favorite.prompt);
    })
    // Check that they exist, in case saved template was removed.
    .filter(Boolean);
    const promptLookupObject = formattedPromptTemplatesWithFavorites.reduce((acc, prompt) => {
      acc[prompt.id] = prompt;
      return acc;
    }, {});
    const featuredTemplateIds = routePrioritiesResponse.templatePriorities[0].templateIds;
    const categoryOrder = (_categoryPriorities$f = categoryPriorities.find(
    // This is set in Chadmin. The string is case sensitive, and a FE means of controlling the order.
    entry => entry.id === 'category_order')) === null || _categoryPriorities$f === void 0 ? void 0 : _categoryPriorities$f.templateIds;
    const featuredTemplates = featuredTemplateIds.map(templateId => {
      return promptLookupObject[templateId];
    })
    // Check that they exist, in case template was deleted but the category was not updated.
    .filter(Boolean);
    const ItemsToHideFromGroup = [
    // Used to grab random templates for empty state. Not shown in as a group. See `chatspot-widget-ui/utils/getEmptyStateTemplates.ts`
    'TEMPLATES_ENABLED_FOR_EMPTY_STATE'];

    // The categories shown
    const orderedCategoryInfo = categoryOrder.reduce((acc, categoryId) => {
      const currentCategory = categoryPriorities.find(entry => entry.id === categoryId);

      // Items not shown in group. If adding cases, please indicate why and where it'll be used - and either add case to MaybeShowExtraInfo
      const shouldSkip = ItemsToHideFromGroup.includes(categoryId);
      if (!shouldSkip && currentCategory) {
        acc.push({
          id: currentCategory.id,
          name: currentCategory.name || '',
          // TODO - @mlegocki: fix TS typing to ensure that these are never blank
          iconName: currentCategory.iconName || '' // TODO - @mlegocki: fix TS typing to ensure that these are never blank
        });
      }
      return acc;
    }, []);

    /** Saved prompts uses the favorites API. We use favorite prompts and then filter
     * out which prompts exist in our prompt library. If they don't exist in our prompt library
     * we assume they are user generated and show them as saved prompts.
     */
    const favoriteUserTemplates = favoritesResponse.filter(favoriteTemplate => {
      return !formattedPromptTemplates.some(template => template.legacyPrompt === favoriteTemplate.prompt);
    }).map(favoriteTemplate => {
      return Object.assign({}, favoriteTemplate, {
        header: favoriteTemplate.prompt,
        isUserGenerated: true,
        isFavorite: true,
        favoriteId: favoriteTemplate.id,
        legacyPrompt: favoriteTemplate.prompt
      });
    });
    const groupedTemplates = categoryPriorities.reduce((acc, category) => {
      if (category.id === 'category_order') {
        return acc;
      }
      const categoryTemplates = category.templateIds.map(templateId => {
        return promptLookupObject[templateId];
      })
      // Check that they exist, in case template was deleted but the category was not updated.
      .filter(Boolean);
      acc[category.id] = categoryTemplates;
      return acc;
    }, {});
    groupedTemplates.FEATURED = featuredTemplates;
    const allFavoritePromptTemplates = [...favoritePromptTemplates, ...favoriteUserTemplates];
    const sortedFavoriteTemplates = sortFavoriteTemplates(allFavoritePromptTemplates);
    groupedTemplates.FAVORITES = sortedFavoriteTemplates;
    return {
      templates: formattedPromptTemplatesWithFavorites,
      featuredTemplates,
      groupedTemplates,
      orderedCategoryInfo
    };
  });
});
export const saveFavoritePromptTemplate = createAsyncThunk('promptTemplates/favoritePromptTemplate', ({
  prompt
}) => saveFavoritePrompt(prompt));
export const removeFavoritePromptTemplate = createAsyncThunk('promptTemplates/unfavoritePromptTemplate', ({
  id
}) => removeFavoritePrompt(id));

/** We will use the favorite API as an MVP
 * We are going to move to a new API that will allow us to save prompts.
 */
export const savePromptTemplate = createAsyncThunk('promptTemplates/savePromptTemplate', ({
  prompt
}) => saveFavoritePrompt(prompt));
export const initialState = {
  status: 'idle',
  draftParameterValues: {},
  prefillValues: {},
  templates: [],
  selectedTemplateAction: undefined,
  featuredTemplates: [],
  groupedTemplates: {},
  selectedTemplate: undefined
};
const promptTemplatesSlice = createSlice({
  name: 'promptTemplates',
  initialState,
  reducers: {
    clearSelectedTemplate: state => {
      state.selectedTemplateAction = initialState.selectedTemplateAction;
      state.selectedTemplate = initialState.selectedTemplate;
      state.draftParameterValues = initialState.draftParameterValues;
      state.prefillValues = initialState.prefillValues;
    },
    draftParameterValueChanged: (state, {
      payload
    }) => {
      if ('id' in payload) {
        // Single key-value update
        state.draftParameterValues[payload.id] = payload.value;
      } else {
        // Multiple key-value update
        state.draftParameterValues = Object.assign({}, state.draftParameterValues, payload.values);
      }
    },
    promptTemplateActionSelected: (state, {
      payload: {
        templateAction
      }
    }) => {
      state.selectedTemplateAction = templateAction;
      state.selectedTemplate = undefined;
      state.draftParameterValues = initialState.draftParameterValues;
      state.prefillValues = {};
    },
    promptTemplateSelected: (state, {
      payload: {
        templateId,
        prefillValues
      }
    }) => {
      state.selectedTemplateAction = undefined;
      state.selectedTemplate = templateId;
      state.draftParameterValues = initialState.draftParameterValues;
      state.prefillValues = prefillValues || {};
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchPromptTemplates.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchPromptTemplates.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(fetchPromptTemplates.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.templates = action.payload.templates;
      state.featuredTemplates = action.payload.featuredTemplates;
      state.groupedTemplates = action.payload.groupedTemplates;
      state.orderedCategoryInfo = action.payload.orderedCategoryInfo;
    });
    builder.addCase(saveFavoritePromptTemplate.fulfilled, (state, action) => {
      const favoritePrompt = state.templates.find(template => template.legacyPrompt === action.payload.prompt);
      if (favoritePrompt) {
        // Updates templates
        favoritePrompt.isFavorite = true;

        // Updates groupedTemplates
        Object.entries(state.groupedTemplates).forEach(([group, templates]) => {
          if (group === 'FAVORITES') {
            // Add the new favorite
            templates.unshift(Object.assign({}, favoritePrompt, {
              isFavorite: true,
              favoriteId: action.payload.id
            }));
          }
          templates.forEach(template => {
            if (template.id === favoritePrompt.id) {
              template.isFavorite = true;
              template.favoriteId = action.payload.id;
            }
          });
        });

        // Updates featuredTemplates
        const favoriteFeatureTemplate = state.featuredTemplates.find(template => template.id === favoritePrompt.id);
        if (favoriteFeatureTemplate) {
          favoriteFeatureTemplate.isFavorite = true;
          favoriteFeatureTemplate.favoriteId = action.payload.id;
        }
      }
    });
    builder.addCase(removeFavoritePromptTemplate.fulfilled, (state, action) => {
      // Updates groupedTemplates
      Object.entries(state.groupedTemplates).forEach(([group, templates]) => {
        if (group === 'FAVORITES') {
          const index = templates.findIndex(template => template.favoriteId === action.meta.arg.id);
          if (index !== -1) {
            templates.splice(index, 1);
          }
        }
        templates.forEach(template => {
          if (template.favoriteId === action.meta.arg.id) {
            template.isFavorite = false;
            template.favoriteId = undefined;
          }
        });
      });

      // Updates templates
      const favoritePrompt = state.templates.find(template => template.favoriteId === action.meta.arg.id);
      if (favoritePrompt) {
        favoritePrompt.isFavorite = false;
        favoritePrompt.favoriteId = undefined;
      }

      // Updates featuredTemplates
      const favoriteFeatureTemplate = state.featuredTemplates.find(template => template.favoriteId === action.meta.arg.id);
      if (favoriteFeatureTemplate) {
        favoriteFeatureTemplate.isFavorite = false;
        favoriteFeatureTemplate.favoriteId = undefined;
      }
    });
    builder.addCase(savePromptTemplate.fulfilled, (state, action) => {
      state.status = 'save_prompt_succeeded';
      Object.entries(state.groupedTemplates).forEach(([group, templates]) => {
        if (group === 'FAVORITES') {
          templates.unshift(Object.assign({}, action.payload, {
            header: action.payload.prompt,
            isUserGenerated: true,
            isFavorite: true,
            favoriteId: action.payload.id,
            legacyPrompt: action.payload.prompt
          }));
        }
      });
    });
    builder.addCase(refetchAndFormatFeaturedTemplates.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(refetchAndFormatFeaturedTemplates.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(refetchAndFormatFeaturedTemplates.fulfilled, (state, action) => {
      const {
        featuredTemplates
      } = action.payload;
      state.status = 'succeeded';
      state.featuredTemplates = featuredTemplates;
      state.groupedTemplates.FEATURED = featuredTemplates;
    });
  }
});
export const {
  clearSelectedTemplate,
  draftParameterValueChanged,
  promptTemplateActionSelected,
  promptTemplateSelected
} = promptTemplatesSlice.actions;
export default promptTemplatesSlice.reducer;